import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { MatchService } from '../service/matchService';
import { LeagueCell, ListSkeleton, ProbabilityBar, StateMessage, TeamCell } from './common';
import { capitalizeName, formatKickoffDay, formatKickoffTime, formatMoney, formatOdds } from '../utils/format';
import { normalizeMatch } from '../utils/matches';

function toPick(row) {
  const match = normalizeMatch(row, { homeImage: 'HomeImagePath', awayImage: 'AwayImagePath' });
  return { ...match, Amount: Number(row.Amount) || 0, Odds: Number(row.Odds) || null };
}

export function PickTable({ userCookie, isDesktop }) {
  const [picks, setPicks] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);
  const [expandedRows, setExpandedRows] = useState(null);
  const toast = useRef(null);

  const load = useCallback(() => {
    setPicks(null);
    setError(null);
    if (!userCookie) {
      setPicks([]);
      return;
    }
    MatchService.getUserPicks(userCookie)
      .then((rows) => setPicks((Array.isArray(rows) ? rows : []).map(toPick)))
      .catch(setError);
  }, [userCookie]);

  useEffect(load, [load]);

  const totals = useMemo(() => {
    const rows = picks || [];
    const now = Date.now();
    return {
      count: rows.length,
      staked: rows.reduce((sum, p) => sum + p.Amount, 0),
      potential: rows.reduce((sum, p) => sum + p.Amount * (p.Odds || 0), 0),
      upcoming: rows.filter((p) => p.kickoff && p.kickoff.getTime() > now).length,
    };
  }, [picks]);

  const onRowEditComplete = (e) => {
    const { newData } = e;
    const updated = { ...newData, PotentialPayout: (newData.Odds || 0) * (newData.Amount || 0), UserCookie: userCookie };
    const previous = picks;
    setPicks(picks.map((p) => (p.PredId === updated.PredId ? updated : p)));
    const { outcomes, best, kickoff, ...payload } = updated;
    MatchService.submitUserPred(payload)
      .then(() => toast.current.show({ severity: 'success', summary: 'Pick updated', life: 2500 }))
      .catch(() => {
        setPicks(previous);
        toast.current.show({ severity: 'error', summary: 'Could not update the pick', life: 5000 });
      });
  };

  const deleteSelected = () => {
    const ids = selected.map((p) => p.PredId);
    const previous = picks;
    setPicks(picks.filter((p) => !ids.includes(p.PredId)));
    setSelected([]);
    MatchService.deleteUserPicks(userCookie, selected.map((p) => ({ PredId: p.PredId })))
      .then(() => toast.current.show({ severity: 'success', summary: `Deleted ${ids.length} pick${ids.length > 1 ? 's' : ''}`, life: 2500 }))
      .catch(() => {
        setPicks(previous);
        toast.current.show({ severity: 'error', summary: 'Could not delete', life: 5000 });
      });
  };

  const confirmDelete = () => confirmDialog({
    message: `Delete ${selected.length} pick${selected.length > 1 ? 's' : ''}? This cannot be undone.`,
    header: 'Delete picks', icon: 'pi pi-trash', acceptClassName: 'p-button-danger', accept: deleteSelected,
  });

  const numberEditor = (options, digits) => (
    <InputNumber value={options.value} onValueChange={(e) => options.editorCallback(e.value)} min={0}
      minFractionDigits={digits} maxFractionDigits={2} inputClassName="w-5rem" />
  );

  const selectionLabel = (p) => {
    const outcome = p.outcomes.find((o) => o.key === p.ResultValue);
    return outcome && outcome.key !== 'D'
      ? <TeamCell name={outcome.Result} image={outcome.Image} />
      : <span className="draw-name">Draw</span>;
  };

  let body;
  if (error) {
    body = <StateMessage icon="pi-exclamation-triangle" title="Your picks could not be loaded"
      detail="The server did not answer." actionLabel="Retry" onAction={load} />;
  } else if (picks === null) {
    body = <ListSkeleton rows={4} />;
  } else if (picks.length === 0) {
    body = (
      <StateMessage icon="pi-bookmark" title="No picks yet"
        detail="Open a match's details and press Pick to track an outcome here." />
    );
  } else {
    body = (
      <>
        <div className="toolbar">
          <Button label={selected.length ? `Delete (${selected.length})` : 'Delete'} icon="pi pi-trash" severity="danger"
            outlined size="small" disabled={!selected.length} onClick={confirmDelete} />
          <span className="toolbar-hint">Edit stake and odds with the pencil on each row.</span>
        </div>
        <div className="table-scroll">
          <DataTable value={picks} dataKey="PredId" className="pick-table" editMode="row"
            onRowEditComplete={onRowEditComplete} selectionMode="checkbox" selection={selected}
            onSelectionChange={(e) => setSelected(e.value)} expandedRows={expandedRows}
            onRowToggle={(e) => setExpandedRows(e.data)} sortField="kickoff" sortOrder={1}
            rowExpansionTemplate={(p) => <div className="p-3"><ProbabilityBar outcomes={p.outcomes} /></div>}>
            <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
            <Column expander style={{ width: '3rem' }} />
            <Column header="Kick-off" field="kickoff" sortable body={(p) => (p.kickoff
              ? <span className="kickoff"><span className="kickoff-time">{formatKickoffTime(p.kickoff)}</span>
                <span className="kickoff-day">{formatKickoffDay(p.kickoff)}</span></span>
              : p.MatchTime)} />
            <Column field="HomeTeamCode" header="Match" body={(p) => (
              <span className="teams-stack">
                <span className="fixture-text">{capitalizeName(p.HomeTeamCode)} v {capitalizeName(p.AwayTeamCode)}</span>
                {isDesktop && <LeagueCell league={p.League} image={p.LeagueImagePath} />}
              </span>
            )} />
            <Column field="ResultValue" header="Pick" body={selectionLabel} />
            <Column header="Stake" field="Amount" sortable body={(p) => formatMoney(p.Amount)}
              editor={(o) => numberEditor(o, 0)} />
            <Column header="Odds" field="Odds" sortable body={(p) => formatOdds(p.Odds)} editor={(o) => numberEditor(o, 2)} />
            <Column field="PotentialPayout" header="Return" body={(p) => formatMoney(p.Amount * (p.Odds || 0))} />
            <Column rowEditor headerStyle={{ width: '6rem' }} bodyStyle={{ textAlign: 'center' }} />
          </DataTable>
        </div>
      </>
    );
  }

  return (
    <section className="panel" aria-live="polite">
      <Toast ref={toast} position={isDesktop ? 'top-right' : 'bottom-center'} />
      <ConfirmDialog />
      {picks && picks.length > 0 && (
        <div className="stat-tiles">
          <div className="stat-tile"><span className="stat-label">Picks</span><span className="stat-value">{totals.count}</span></div>
          <div className="stat-tile"><span className="stat-label">Upcoming</span><span className="stat-value">{totals.upcoming}</span></div>
          <div className="stat-tile"><span className="stat-label">Staked</span><span className="stat-value">{formatMoney(totals.staked)}</span></div>
          <div className="stat-tile"><span className="stat-label">Potential return</span><span className="stat-value">{formatMoney(totals.potential)}</span></div>
        </div>
      )}
      {body}
      {picks && picks.length === 0 && !error && <p className="text-center"><Link to="/">Browse today's matches</Link></p>}
    </section>
  );
}
